import { useState } from 'react';
import { X, DollarSign, Check, CreditCard, Zap, ArrowRight } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';

const PRESET_AMOUNTS = ['50', '75', '100', '150', '200'];

export default function BillingFlow({ provider, onClose }) {
  const { savedCards } = useAppState();

  const [step, setStep] = useState('amount'); // 'amount' | 'payment' | 'confirm' | 'success'
  const [amount, setAmount] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(savedCards?.[0] || null);

  if (!provider) return null;

  const cashbackPercent = 3; // 3% cashback on bill payments
  const cashbackAmount = amount ? (parseFloat(amount) * cashbackPercent / 100).toFixed(2) : '0.00';

  const handleClose = () => {
    setStep('amount');
    setAmount('');
    onClose();
  };

  const handleConfirmPayment = () => {
    setStep('success');
  };

  // Step: Amount Entry
  if (step === 'amount') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Pay Bill</h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Provider Info */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <h3 className="font-bold text-gray-900 mb-1">{provider}</h3>
                <p className="text-indigo-600 font-bold text-xs uppercase tracking-tight">Group Billing Active</p>
              </div>

              {/* Preset Amounts */}
              <div className="space-y-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Quick Amounts</p>
                <div className="grid grid-cols-3 gap-2">
                  {PRESET_AMOUNTS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setAmount(preset)}
                      className={`py-3 rounded-xl font-bold text-sm transition-all ${
                        amount === preset
                          ? 'bg-indigo-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      ${preset}
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="mt-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Enter Bill Amount</p>
                  <div className="relative">
                    <DollarSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full bg-gray-100 px-5 py-3 pl-10 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              {amount && parseFloat(amount) >= 10 && (
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-gray-700">Bill Amount</span>
                    <span className="text-sm font-bold text-gray-900">${amount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-700">Cashback ({cashbackPercent}%)</span>
                    <span className="text-sm font-bold text-emerald-600">+${cashbackAmount}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setStep('payment')}
                disabled={!amount || parseFloat(amount) < 10}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                Select Payment <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Payment Method Selection
  if (step === 'payment') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Payment Method</h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Bill Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-gray-900">{provider}</h3>
                    <p className="text-xs text-gray-500">Monthly Bill Payment</p>
                  </div>
                  <span className="text-lg font-bold text-indigo-600">${amount}</span>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Payment Method</p>
                {savedCards?.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setSelectedPayment(card)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selectedPayment?.id === card.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white'
                    }`}
                  >
                    <div className="w-10 h-7 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                      <CreditCard size={14} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 capitalize text-sm">{card.type} •••• {card.last4}</p>
                      <p className="text-xs text-gray-500">Expires {card.expiry}</p>
                    </div>
                    {selectedPayment?.id === card.id && <Check size={18} className="text-indigo-600" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setStep('confirm')}
                disabled={!selectedPayment}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                Review Payment <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Confirmation
  if (step === 'confirm') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-bold">Confirm Payment</h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <p className="font-bold text-gray-900 mb-1">{provider}</p>
                  <p className="text-indigo-600 font-bold text-xs uppercase tracking-tight">Monthly Bill Payment</p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Bill Amount</span>
                    <span className="text-sm font-bold text-gray-900">${amount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Payment Method</span>
                    <span className="text-sm font-bold text-gray-900 capitalize">
                      {selectedPayment?.type} •••• {selectedPayment?.last4}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cashback Earned</span>
                    <span className="text-sm font-bold text-emerald-600">+${cashbackAmount}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-indigo-600">${amount}</span>
                  </div>
                </div>
              </div>

              {/* Cashback Info */}
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                  <Zap size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-sm text-emerald-800">Group Billing Bonus</p>
                  <p className="text-xs text-emerald-600">Earn {cashbackPercent}% cashback on every bill payment</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleConfirmPayment}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm"
              >
                Pay ${amount}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Success
  if (step === 'success') {
    return (
      <>
        <div className="backdrop animate-fade-in" onClick={handleClose} />
        <div className="slide-panel">
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Complete!</h2>
              <p className="text-gray-500 text-sm mb-6">
                Your {provider} bill has been paid successfully.
              </p>

              {/* Receipt Card */}
              <div className="bg-gray-50 p-4 rounded-xl text-left mb-5">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Amount Paid</span>
                  <span className="text-lg font-bold text-gray-900">${amount}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Cashback Earned</span>
                  <span className="text-base font-bold text-emerald-600">+${cashbackAmount}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-500">Payment Method</span>
                  <span className="text-sm font-bold text-gray-900 capitalize">
                    {selectedPayment?.type} •••• {selectedPayment?.last4}
                  </span>
                </div>
              </div>

              {/* Cashback Banner */}
              <div className="bg-emerald-50 px-4 py-2 rounded-full inline-block">
                <span className="text-emerald-700 font-bold text-sm">+${cashbackAmount} added to your wallet!</span>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleClose}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
}
